import axios from 'axios';
import {
    PATIENT_LIST_REQUEST, PATIENT_LIST_SUCCESS, PATIENT_LIST_FAIL,
    PATIENT_LIST_BY_IDS_REQUEST, PATIENT_LIST_BY_IDS_SUCCESS, PATIENT_LIST_BY_IDS_FAIL,
    PATIENT_DETAILS_REQUEST, PATIENT_DETAILS_SUCCESS, PATIENT_DETAILS_FAIL,
    PATIENT_CREATE_REQUEST, PATIENT_CREATE_SUCCESS, PATIENT_CREATE_FAIL,
    PATIENT_UPDATE_REQUEST, PATIENT_UPDATE_SUCCESS, PATIENT_UPDATE_FAIL,
    PATIENT_DELETE_REQUEST, PATIENT_DELETE_SUCCESS, PATIENT_DELETE_FAIL,
    PATIENT_DTO_REQUEST, PATIENT_DTO_SUCCESS, PATIENT_DTO_FAIL,
    PATIENT_SUBJECTS_REQUEST, PATIENT_SUBJECTS_SUCCESS, PATIENT_SUBJECTS_FAIL,
} from '../constants/patientConstants';
import { formatApiError } from '../utils/formatApiError';

// Helper functions
const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    if (error.response?.status === 403) {
        // formatApiError already embeds policy when present — do not append again.
        return [formatApiError(error)];
    }
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

const API_BASE_URL = '/dotnet_api/v1/patient';

export const listPatients = (params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_LIST_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/all`, config);

        dispatch({ type: PATIENT_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_LIST_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const listPatientsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_LIST_BY_IDS_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/ids/all`,
            ids,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PATIENT_LIST_BY_IDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_LIST_BY_IDS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

// New paginated endpoint for patient subjects
export const getPatientSubjects = (patientId, params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_SUBJECTS_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/${patientId}/subjects`, config);
        dispatch({ type: PATIENT_SUBJECTS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_SUBJECTS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getPatientDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_DETAILS_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PATIENT_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_DETAILS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const createPatient = (patientData) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_CREATE_REQUEST });
        const { data } = await axios.post(
            API_BASE_URL,
            patientData,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PATIENT_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_CREATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const updatePatient = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_UPDATE_REQUEST });
        const { data } = await axios.patch(
            `${API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PATIENT_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const deletePatient = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_DELETE_REQUEST });
        await axios.delete(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PATIENT_DELETE_SUCCESS });
    } catch (error) {
        dispatch({ type: PATIENT_DELETE_FAIL, payload: formatErrorMessage(error) });
    }
};

export const loadPatientDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: PATIENT_DTO_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/dto`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PATIENT_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PATIENT_DTO_FAIL,
            payload: formatErrorMessage(error)
        });
    }
}; 